/**
 * Platform-neutral `spreadsheetSpec` semantics.
 *
 * Deliberately free of DOM and react-native imports so the desktop web editor
 * and the mobile read-only viewer format cells identically. Number formatting
 * used to live only inside the `.web.jsx` editor, so a native viewer had no way
 * to agree with it except by copying.
 */
import { evaluateFormula, columnLabel } from '@/lib/spreadsheetFormulas';

export { columnLabel };

export const MAX_SHEETS = 8;
export const MAX_ROWS = 500;
export const MAX_COLS = 40;

export const ROW_HEIGHT = 30;
export const ROW_HEADER_WIDTH = 44;
export const DEFAULT_COL_WIDTH = 108;

// `columns[i].width` is an approximate character count, the same unit Excel
// uses, so it round-trips through the .xlsx export unchanged.
export const COL_WIDTH_PER_CHAR = 7.5;
export const MIN_COL_WIDTH = 48;
export const MAX_COL_WIDTH = 600;

export const DEFAULT_SPREADSHEET_ACCENT = 'blue';

export function spreadsheetBlockLimitMessage({ startRow = 0, startCol = 0, rows = 0, columns = 0 } = {}) {
  if (startRow + rows > MAX_ROWS) {
    return `This operation would exceed the ${MAX_ROWS}-row editor limit. No cells were changed.`;
  }
  if (startCol + columns > MAX_COLS) {
    return `This operation would exceed the ${MAX_COLS}-column editor limit. No cells were changed.`;
  }
  return '';
}

// Imported workbooks can use dark fills as well as the editor's pastel palette.
export function spreadsheetFillTextColor(background, fallback = '#1f2937') {
  const match = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(String(background || ''));
  if (!match) return fallback;
  const hex = match[1].length === 3 ? [...match[1]].map(c => c + c).join('') : match[1];
  const channels = [0, 2, 4].map(i => {
    const value = parseInt(hex.slice(i, i + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  return luminance < 0.179 ? '#ffffff' : '#111111';
}

export function coerceSpreadsheetInput(raw, format) {
  const original = String(raw ?? '').slice(0, 500);
  if (format === 'text') return original;
  const text = original.trim();
  if (!text) return '';
  if (/^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text)) {
    const digits = text.replace(/,/g, '');
    if (/^-?0\d/.test(digits)) return original;
    const number = Number(digits);
    if (Number.isInteger(number) && !Number.isSafeInteger(number)) return original;
    if (Number.isFinite(number)) return number;
  }
  if (text === 'true') return true;
  if (text === 'false') return false;
  return original;
}

export const SPREADSHEET_ACCENTS = {
  blue: '#2563eb',
  green: '#15803d',
  slate: '#334155',
  red: '#b91c1c',
  amber: '#b45309',
  violet: '#6d28d9',
};

export const getSpreadsheetAccent = (name) => (
  Object.prototype.hasOwnProperty.call(SPREADSHEET_ACCENTS, name)
    ? SPREADSHEET_ACCENTS[name]
    : SPREADSHEET_ACCENTS[DEFAULT_SPREADSHEET_ACCENT]
);

/**
 * Render a cell for display. Only numbers are formatted; `fmt: 'date'` values
 * arrive as ISO strings and are shown verbatim, which is what the .xlsx export
 * writes for non-numeric dates too.
 */
export const formatCellValue = (cell) => {
  const value = cell?.v;
  if (value === '' || value === null || value === undefined) return '';
  if (typeof value === 'number') {
    switch (cell.fmt) {
      case 'currency':
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      case 'percent':
        return `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
      case 'number':
        return value.toLocaleString('en-US', { maximumFractionDigits: 3 });
      default:
        return value.toLocaleString('en-US', { maximumFractionDigits: 6 });
    }
  }
  return String(value);
};

// Spreadsheet generators do not always include the editor's compact `fmt`
// metadata even when the exported XLSX has a number format. Recover the most
// common case conservatively: a clearly percentage-like column whose numeric
// values are ratios. This keeps an imported "Conversion Rate" column from
// rendering as 0.157143 while avoiding guesses for arbitrary numeric columns.
const PERCENT_HEADER_RE = /(?:^|[\s_(/-])(?:percent(?:age)?|rate|ratio|share|margin)(?:$|[\s_)/-]|%)/i;

export function inferMissingCellFormats(rows, { headerRow = true } = {}) {
  if (!headerRow || !Array.isArray(rows) || rows.length < 2) return rows;
  const headers = rows[0] || [];
  const percentColumns = new Set();

  headers.forEach((header, columnIndex) => {
    if (!PERCENT_HEADER_RE.test(String(header?.v ?? ''))) return;
    const values = rows
      .slice(1)
      .map(row => row?.[columnIndex]?.v)
      .filter(value => value !== '' && value !== null && value !== undefined);
    if (values.length && values.every(value => (
      typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1
    ))) {
      percentColumns.add(columnIndex);
    }
  });

  if (!percentColumns.size) return rows;
  return rows.map((row, rowIndex) => row.map((cell, columnIndex) => {
    if (rowIndex === 0 || !percentColumns.has(columnIndex) || cell?.fmt) return cell;
    return typeof cell?.v === 'number' ? { ...cell, fmt: 'percent' } : cell;
  }));
}

/** Numbers right-align by default; an explicit `align` always wins. */
export const cellTextAlign = (cell) => {
  if (cell?.align === 'center') return 'center';
  if (cell?.align === 'right') return 'right';
  if (cell?.align === 'left') return 'left';
  return typeof cell?.v === 'number' ? 'right' : 'left';
};

/** Rendered pixel width for a column, honoring its character-count width. */
export const columnPixelWidth = (column) => {
  const chars = Number(column?.width) || 0;
  if (!chars) return DEFAULT_COL_WIDTH;
  return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, chars * COL_WIDTH_PER_CHAR));
};

/**
 * Total pixel width of a rendered sheet, including the sticky row-number
 * gutter. Used so the grid table keeps its column sizes and overflows the
 * panel (which can then scroll horizontally) instead of shrinking to fit.
 */
export const sheetTablePixelWidth = (columns, columnCount) => {
  const count = Math.max(
    Number.isFinite(columnCount) ? columnCount : 0,
    Array.isArray(columns) ? columns.length : 0,
  );
  let width = ROW_HEADER_WIDTH;
  for (let i = 0; i < count; i += 1) {
    width += columnPixelWidth(columns?.[i]);
  }
  return width;
};

/**
 * Shape an incoming spec for read-only rendering: pad every sheet to a
 * rectangle so the grid has no holes, and resolve formula cells so `v` holds
 * the value to display. Specs are normalized server-side on save, so this
 * guards against a malformed payload rather than re-validating every field.
 */
export const normalizeWorkbookForViewing = (spec, fallbackTitle = 'Spreadsheet') => {
  const sheets = Array.isArray(spec?.sheets) ? spec.sheets : [];
  const normalizedSheets = sheets.map((sheet, index) => {
    const sourceRows = Array.isArray(sheet?.rows) ? sheet.rows : [];
    const columnCount = Math.max(
      1,
      ...sourceRows.map(row => (Array.isArray(row) ? row.length : 0)),
      Array.isArray(sheet?.columns) ? sheet.columns.length : 0,
    );
    return {
      id: sheet?.id || `sheet-${index + 1}`,
      name: String(sheet?.name || `Sheet ${index + 1}`),
      headerRow: sheet?.headerRow !== false,
      columns: Array.from(
        { length: Math.min(columnCount, MAX_COLS) },
        (_, ci) => (Array.isArray(sheet?.columns) ? sheet.columns[ci] : null) || {},
      ),
      rows: sourceRows.map((row) => {
        const cells = Array.isArray(row) ? row.slice(0, MAX_COLS) : [];
        return Array.from({ length: Math.min(columnCount, MAX_COLS) }, (_, ci) => cells[ci] || { v: '' });
      }),
    };
  });

  const sheetIndexByName = new Map();
  normalizedSheets.forEach((sheet, index) => {
    sheetIndexByName.set(sheet.name.toLowerCase(), index);
    sheetIndexByName.set(String(sheet.id).toLowerCase(), index);
  });
  const memo = new Map();
  const inProgress = new Set();
  const resolveCell = (ref, currentSheetIndex) => {
    const requestedSheet = String(ref?.sheet || '').trim().toLowerCase();
    const sheetIndex = requestedSheet
      ? sheetIndexByName.get(requestedSheet)
      : currentSheetIndex;
    if (!Number.isInteger(sheetIndex)) return '#REF';
    const key = `${sheetIndex}:${ref.r},${ref.c}`;
    if (memo.has(key)) return memo.get(key);
    const cell = normalizedSheets[sheetIndex]?.rows?.[ref.r]?.[ref.c];
    if (!cell) return '';
    if (typeof cell.f === 'string' && cell.f.startsWith('=')) {
      if (inProgress.has(key)) return '#CYCLE';
      inProgress.add(key);
      const value = evaluateFormula(
        cell.f.slice(1),
        nestedRef => resolveCell(nestedRef, sheetIndex),
      );
      inProgress.delete(key);
      memo.set(key, value);
      return value;
    }
    const value = cell.v ?? '';
    memo.set(key, value);
    return value;
  };

  return {
    title: String(spec?.title || fallbackTitle),
    accent: getSpreadsheetAccent(spec?.accent),
    sheets: normalizedSheets.map((sheet, sheetIndex) => {
      const computedRows = sheet.rows.map((row, r) => row.map((cell, c) => {
        if (typeof cell?.f !== 'string' || !cell.f.startsWith('=')) return cell;
        const value = resolveCell({ r, c }, sheetIndex);
        return value === cell.v ? cell : { ...cell, v: value };
      }));
      return {
        ...sheet,
        rows: inferMissingCellFormats(computedRows, { headerRow: sheet.headerRow }),
      };
    }),
  };
};
